import React from 'react';

interface Params {
  min?: number;
  value?: number;
  max?: number;
  onChange?: (value: number) => void;
}

export const useDraggable = ({
  min = 0,
  value = 0,
  max = 100,
  onChange,
}: Params) => {
  const sliderRef = React.useRef<HTMLDivElement | null>(null);
  const grabTargetRef = React.useRef<HTMLDivElement | null>(null);
  const grabHandleRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const slider = sliderRef.current;
    const grabTarget = grabTargetRef.current;
    const grabHandle = grabHandleRef.current;

    if (!slider) {
      return;
    }

    slider.setAttribute('style', `--min: ${min}%; --value: ${value}%`);

    if (!grabTarget || !grabHandle) {
      return;
    }

    const onMouseDown = (event: MouseEvent) => {
      event.preventDefault();

      let shiftX = event.clientX - grabHandle.getBoundingClientRect().left - 15;

      const onMouseMove = (event: MouseEvent) => {
        let value =
          event.clientX - shiftX - slider.getBoundingClientRect().left;
        const maxLimit = slider.offsetWidth;
        const minLimit = maxLimit * (min / 100);

        if (value < minLimit) {
          value = minLimit;
        }

        if (value > maxLimit) {
          value = maxLimit;
        }

        const percent = (value / maxLimit) * 100;

        if (Math.round(percent) > max) {
          return;
        }

        slider.setAttribute('style', `--min: ${min}%; --value: ${percent}%`);

        onChange?.(Math.round(percent));
      };

      const onMouseUp = () => {
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('mousemove', onMouseMove);
      };

      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('mousemove', onMouseMove);
    };

    const onDragStart = () => false;

    grabHandle.addEventListener('mousedown', onMouseDown);
    grabHandle.addEventListener('dragstart', onDragStart);

    return () => {
      grabHandle.removeEventListener('mousedown', onMouseDown);
      grabHandle.removeEventListener('dragstart', onDragStart);
    };
  }, [min, onChange, value]);

  return {
    sliderRef,
    grabTargetRef,
    grabHandleRef,
  };
};
